import { apiFetch } from './client';
import { parseActivity } from '../components/agenda/agendaHelpers';

/**
 * `GET /api/activity` — each agent's standing work, in one answer.
 *
 * Served by `machinai` itself from 0.62.0, not by an extension, so every
 * instance has it and no capability gates it. It needs the `editor` role
 * (403 below that), and an older core answers 404.
 *
 * **A section reports its own absence.** Each of the four carries
 * `available` and, when that is false, a `reason` and empty lists: goals
 * that are switched off for an agent, or a store that could not be read.
 * An unavailable section is not an empty one, and the page renders the
 * reason rather than an empty list that reads as "nothing to do".
 *
 * Times are epoch milliseconds, and `now_ms` is the server's clock at the
 * moment it answered. The page measures every "in 2h" and "3d ago" from
 * it rather than from the browser's clock, so a skewed laptop does not
 * show a live grant as lapsed — the server decided what is live by its
 * own clock.
 */

/**
 * Every status a goal can hold (`machinai/core/goals.py`, `GoalStatus`).
 * `active` and `paused` are open; the other four have ended.
 */
export type GoalStatus =
  | 'active'
  | 'paused'
  | 'done'
  | 'blocked'
  | 'expired'
  | 'cleared';

export interface Goal {
  /** Twelve hex characters. `/goals` matches it exactly, not by prefix. */
  id: string;
  objective: string;
  status: GoalStatus;
  /** The owner let this goal's turns change things (ADR 052). */
  may_write: boolean;
  max_turns: number;
  turns_used: number;
  created_ms: number;
  deadline_ms: number;
  /**
   * When the next turn is due. Meaningful only while `active`: pausing,
   * resuming and clearing all set it to the moment of the change.
   */
  next_run_ms: number;
  /**
   * The agent's last report — `continue`, `wait`, `done` or `blocked` —
   * or `""` before its first. A string rather than a union because the
   * store keeps whatever the report tool accepted.
   */
  last_report: string;
  /** The summary that came with that report; `""` before the first. */
  last_summary: string;
  updated_ms: number;
}

export interface GoalsSection {
  available: boolean;
  /** Why the section is unavailable; `null` when it is available. */
  reason: string | null;
  /** Active and paused, oldest first. */
  open: Goal[];
  /** Ended in the last seven days, newest first. */
  ended: Goal[];
}

/** A tool call held for the owner's answer. */
export interface PendingApproval {
  /** A uuid hex. `/approvals` accepts any unambiguous prefix of it. */
  id: string;
  tool_name: string;
  tool_args: Record<string, unknown>;
  /** What the gate asked, as the owner was asked it. */
  prompt: string;
  session_id: string;
  channel_id: string;
  /**
   * `user` for an owner's turn. Anything else is an unattended one —
   * `scheduled` for a schedule's or a goal's turn.
   */
  message_source: string;
  created_ms: number;
  /**
   * When it lapses unanswered. A lapsed approval resolves to deny and can
   * no longer be approved, so the page offers only the deny command for
   * one whose expiry has passed.
   */
  expires_ms: number;
  /** The gates that asked, in order. */
  gates: string[];
  /**
   * Whether `approve <id> session` / `hour` may answer it (ADR 062). A
   * gate that refuses a grant — a public-scope write, for one — makes the
   * whole approval answerable once only.
   */
  grantable: boolean;
}

/**
 * How a decided approval ended (`machinai/core/approvals.py`,
 * `_DECIDED_STATES`). `pending` is never decided, so it is not here.
 */
export type ApprovalDecision = 'granted' | 'denied' | 'expired';

export interface DecidedApproval {
  id: string;
  tool_name: string;
  state: ApprovalDecision;
  /**
   * Who answered: a channel actor such as `slash:…`, `grant:<id>` when a
   * standing grant allowed the call without asking, or `""` when nothing
   * was recorded.
   */
  resolved_by: string;
  /** `""` when no reason was given. */
  resolution_reason: string;
  created_ms: number;
  /**
   * `null` only if the record was never resolved, which a decided row
   * should not be; the core's record type allows it, so this does too.
   */
  resolved_ms: number | null;
}

export interface ApprovalsSection {
  available: boolean;
  reason: string | null;
  pending: PendingApproval[];
  /** Newest first, at most twenty. */
  decided: DecidedApproval[];
}

/** A standing grant: later calls of one tool, asked by the same gates. */
export interface Grant {
  /** A uuid hex. `/approvals revoke` accepts an unambiguous prefix. */
  id: string;
  /** `session` covers one session for at most 24 h; `agent` one hour. */
  scope: 'session' | 'agent';
  tool_name: string;
  gates: string[];
  /** The session a `session` grant covers; `null` for `agent`. */
  session_id: string | null;
  /** The approval whose answer made it. */
  approval_id: string;
  /** Who answered that approval; `""` when nothing was recorded. */
  created_by: string;
  created_ms: number;
  expires_ms: number;
  /** How many calls it has allowed so far. */
  uses: number;
}

export interface GrantsSection {
  available: boolean;
  reason: string | null;
  /** Unrevoked and unexpired. */
  live: Grant[];
}

/** A schedule the agent set for itself (`schedule_create`). */
export interface Schedule {
  id: string;
  name: string;
  /** `task` runs its prompt through the agent; `reminder` sends it verbatim. */
  kind: 'task' | 'reminder';
  /** `HH:MM`. */
  time: string;
  /** Lower-case weekday names; empty means daily. */
  days: string[];
  /** `YYYY-MM-DD` for a one-time schedule, else `null`. */
  once_date: string | null;
  /** A one-time schedule is disabled once it has run. */
  enabled: boolean;
  /**
   * The date it last ran, `YYYY-MM-DD`. `null` when it never has — and
   * `""` after the scheduler resets a run it could not confirm, which
   * also reads as "never".
   */
  last_run: string | null;
}

export interface SchedulesSection {
  available: boolean;
  reason: string | null;
  entries: Schedule[];
}

export interface AgentActivity {
  agent_id: string;
  goals: GoalsSection;
  approvals: ApprovalsSection;
  grants: GrantsSection;
  schedules: SchedulesSection;
}

export interface ActivityResponse {
  /** The server's clock when it answered. */
  now_ms: number;
  /** In the runtime's declaration order. */
  agents: AgentActivity[];
}

/**
 * The whole answer, checked at the edge.
 *
 * `parseActivity` refuses an answer whose sections are not the shape
 * above, naming the first field that is wrong. Without it, a mismatch
 * with the route would surface as a render-time `TypeError`, and with no
 * error boundary that blanks the whole panel rather than this page.
 */
export const fetchActivity = (): Promise<ActivityResponse> =>
  apiFetch<unknown>('/api/activity').then(parseActivity);
